import React from 'react';
import { Spin, Modal, Table, Button, Flex, Typography, Space } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { UserWithRole, Invitation } from '../../types';
import TgAvatar from './TgAvatar';
import { useGetProjectDetailsQuery } from '../store/projects';

import { USER_ROLES, PROJECT_TYPES } from '../constants';

const { Title } = Typography;

interface ProjectUsersProps {
    open: boolean;
    projectId: number;
    onClose: () => void;
    isOwner: boolean;
    onAddInvitation: (projectId: number) => void;
    onRemoveUserFromProject: (projectId: number, userId: number) => void;
    onDeleteInvitation: (projectId: number, username: string) => void;
}

const ProjectUsers: React.FC<ProjectUsersProps> = ({
    open,
    onClose,
    isOwner,
    onAddInvitation,
    onRemoveUserFromProject,
    onDeleteInvitation,
    projectId,
}) => {
    const { data, isLoading, error } = useGetProjectDetailsQuery({ projectId });

    if (isLoading) {
        return <Spin />;
    }

    if (error || !data) {
        return <div>:(</div>;
    }

    const projectDetails = data.message;

    const userColumns: ColumnsType<UserWithRole> = [
        {
            title: 'Пользователь',
            dataIndex: 'user',
            key: 'user',
            render: (_, record) => (
                <Space>
                    <TgAvatar user={record} />
                    <div>
                        <div>{record.firstName} {record.lastName || ''}</div>
                        <div style={{ fontWeight: 400, opacity: 0.5, lineHeight: 1 }} >
                            @{record.username}
                        </div>
                    </div>
                </Space>
            ),
        },
        {
            title: 'Роль',
            dataIndex: 'role',
            key: 'role',
            render: (role) => USER_ROLES[role],
        },
        ...(isOwner
            ? [{
                title: 'Действия',
                key: 'action',
                render: (_: any, record: UserWithRole) => (
                    <Button
                        icon={<DeleteOutlined />}
                        disabled={record.role === USER_ROLES.owner}
                        onClick={() => onRemoveUserFromProject(projectId, record.userId)}
                        danger
                    >
                        Удалить
                    </Button>
                ),
            }]
            : []),
    ];

    const invitationColumns: ColumnsType<Invitation> = [
        {
            title: 'Имя пользователя',
            dataIndex: 'username',
            key: 'username',
            render: (username) => `@${username}`,
        },
        {
            title: 'Роль',
            dataIndex: 'role',
            key: 'role',
            render: (role) => USER_ROLES[role],
        },
        ...(isOwner
            ? [{
                title: 'Действия',
                key: 'action',
                render: (_: any, record: Invitation) => (
                    <Button
                        icon={<DeleteOutlined />}
                        onClick={() => onDeleteInvitation(projectId, record.username)}
                        danger
                    >
                        Удалить
                    </Button>
                ),
            }]
            : []),
    ];

    return (
        <Modal
            title={`Участники проекта «${projectDetails.name}»`}
            open={open}
            onCancel={onClose}
            footer={null}
            width={800}
        >
            <Flex vertical gap="large">
                <div>
                    <Title level={4}>Пользователи</Title>
                    <Table
                        columns={userColumns}
                        dataSource={projectDetails.users}
                        rowKey="userId"
                        pagination={false}
                    />
                </div>
                <div>
                    <Flex justify="space-between" align="center">
                        <Title level={4}>Приглашения</Title>
                        {isOwner && (
                            <Button type="primary" onClick={() => onAddInvitation(projectId)}>
                                Отправить приглашение
                            </Button>
                        )}
                    </Flex>
                    <Table
                        columns={invitationColumns}
                        dataSource={projectDetails.invitations}
                        rowKey="username"
                        pagination={false}
                    />
                </div>
            </Flex>
        </Modal>
    );
};

export default ProjectUsers;
